import { ref } from 'vue'

import {
  createVariantTreeBOMValidation,
  getVariantTreeBOMValidationDetails
} from '@/api'

const mapAmbiguousElementsToDetails = (ambiguousElements = []) => ambiguousElements.map(
  ({ ambiguous1Id2 = '', ambiguous2Id2 = '' } = {}) => `${ ambiguous1Id2 } - ${ ambiguous2Id2 }`
)

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

export function useAmbiguousDetails({ tableHeader }) {
  const ambiguousRowsState = ref({})

  const getAmbiguousRowState = rowId => ambiguousRowsState.value[rowId] || {
    isChecking: false,
    isExpanded: false,
    details: [],
    bomValidationStatus: null,
    bomValidationCorrelationId: null,
    hasFetchedDetails: false
  }

  const setAmbiguousRowState = (rowId, newState = {}) => {
    ambiguousRowsState.value = {
      ...ambiguousRowsState.value,
      [rowId]: {
        ...getAmbiguousRowState(rowId),
        ...newState
      }
    }
  }

  const getHeaderColumnFromCell = cell => {
    const field = cell?.getField?.()

    if (!field) return null

    const normalizedField = field.replace(/-/g, '.')

    return tableHeader.value.find(
      ({ field: headerField } = {}) => headerField === normalizedField
    ) || null
  }

  const getComponentIdFromCell = cell => {
    const headerColumn = getHeaderColumnFromCell(cell)

    return headerColumn?.type === 'component'
      ? headerColumn.codeId
      : null
  }

  const pollBomValidationStatus = async ({
    combinationId,
    correlationId,
    maxAttempts = 20,
    delay = 2000
  }) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // eslint-disable-next-line no-await-in-loop
      const response = await getVariantTreeBOMValidationDetails({ combinationId, correlationId })

      if (response.status === 'SUCCESS') {
        return response
      }

      if (response.status === 'FAILED') {
        throw new Error('BOM validation failed')
      }

      // eslint-disable-next-line no-await-in-loop
      await wait(delay)
    }

    throw new Error('BOM validation polling timed out')
  }

  const getAmbiguousDetails = async ({ rowData, componentId }) => {
    const {
      combinationId,
      precondition,
      bomValidationStatus,
      bomValidationCorrelationId
    } = rowData

    let response = null
    let correlationId = bomValidationCorrelationId

    if (bomValidationStatus === 'SUCCESS' && correlationId) {
      response = await getVariantTreeBOMValidationDetails({ combinationId, correlationId })

      return {
        details: mapAmbiguousElementsToDetails(response.ambiguousElements),
        correlationId,
        status: response.status
      }
    }

    if (bomValidationStatus === 'IN_PROGRESS' && correlationId) {
      response = await pollBomValidationStatus({
        combinationId,
        correlationId
      })

      return {
        details: mapAmbiguousElementsToDetails(response.ambiguousElements),
        correlationId,
        status: response.status
      }
    }

    const createResponse = await createVariantTreeBOMValidation({
      combinationId,
      componentId,
      precondition
    })

    correlationId = createResponse.correlationId

    response = await pollBomValidationStatus({
      combinationId,
      correlationId
    })

    return {
      details: mapAmbiguousElementsToDetails(response.ambiguousElements),
      correlationId,
      status: response.status
    }
  }

  const handleCheckDetailsClick = async ({ row, cell, refreshRow }) => {
    const rowData = row.getData()
    const rowState = getAmbiguousRowState(rowData.id)

    if (rowState.isExpanded) {
      setAmbiguousRowState(rowData.id, {
        isExpanded: false
      })

      refreshRow(row)
      return
    }

    if (rowState.isChecking) return

    if (rowState.hasFetchedDetails && rowState.details.length) {
      setAmbiguousRowState(rowData.id, {
        isExpanded: true
      })

      refreshRow(row)
      return
    }

    const componentId = getComponentIdFromCell(cell)

    setAmbiguousRowState(rowData.id, {
      isChecking: true,
      isExpanded: false
    })

    refreshRow(row)

    try {
      const { details, correlationId, status } = await getAmbiguousDetails({
        rowData,
        componentId
      })

      await row.update({
        bomValidationCorrelationId: correlationId,
        bomValidationStatus: status
      })

      setAmbiguousRowState(rowData.id, {
        isChecking: false,
        isExpanded: true,
        details,
        hasFetchedDetails: true,
        bomValidationCorrelationId: correlationId,
        bomValidationStatus: status
      })
    }
    catch (error) {
      setAmbiguousRowState(rowData.id, {
        isChecking: false,
        isExpanded: false
      })
    }
    finally {
      refreshRow(row)
    }
  }

  return {
    ambiguousRowsState,
    getAmbiguousRowState,
    setAmbiguousRowState,
    getComponentIdFromCell,
    getHeaderColumnFromCell,
    pollBomValidationStatus,
    getAmbiguousDetails,
    handleCheckDetailsClick
  }
}
